import { WeatherType } from '../renderer/atmosphere/weather';

// Asset host override. Empty in dev (assets served by Vite from /public);
// set to the R2 custom domain in production so the >25 MB GLB + lightmaps
// load from Cloudflare R2 instead of Pages.
const ASSET_BASE_URL =
  (import.meta.env.VITE_ASSET_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export interface MapDefinition {
  id: string;
  label: string;
  arenaHalf: number;
  defaultWeather: WeatherType;
  kind: 'graphical-asset';
  scenePath: string;
}

export const MAP_REGISTRY: Record<string, MapDefinition> = {
  de_outpost_v6: {
    id: 'de_outpost_v6',
    kind: 'graphical-asset',
    label: 'Outpost FOB',
    arenaHalf: 80,
    scenePath: `${ASSET_BASE_URL}/maps/de_outpost_v6/scene.glb`,
    defaultWeather: WeatherType.Clear,
  },
  // v7 placement-fix pass on top of v6 — same arena, same weather, swapped
  // scene.glb. See docs/de_outpost_v7_fixes.md for the ~60 placement deltas.
  // Opt-in via `?map=de_outpost_v7` URL param; v6 stays the default until
  // v7 ships its baked lightmaps + interactive config.
  de_outpost_v7: {
    id: 'de_outpost_v7',
    kind: 'graphical-asset',
    label: 'Outpost FOB v7',
    arenaHalf: 80,
    scenePath: `${ASSET_BASE_URL}/maps/de_outpost_v7/scene.glb`,
    defaultWeather: WeatherType.Clear,
  },
};

// de_outpost_v7 is the default map as of the v7 ship (PR #6). v6 stays
// registered so URL-bound deep links from old sessions (`?map=de_outpost_v6`)
// keep working until the next graphics generation forces a hard cut.
export const DEFAULT_MAP_ID = 'de_outpost_v7';

export const MAP_IDS = Object.keys(MAP_REGISTRY);

export function setActiveMapId(_id: string): void {
  // Single-map build: selection is a no-op. Kept for callsite stability.
}

export function getRequestedMapId(search = window.location.search): string {
  // Honour `?map=<id>` for opt-in alternate maps (v7 currently). Unknown
  // ids fall back to the default so a stale URL never breaks the loader.
  try {
    const params = new URLSearchParams(search);
    const requested = params.get('map');
    if (requested && MAP_REGISTRY[requested]) {
      return requested;
    }
  } catch {
    // Bad query string — fall through to default.
  }
  return DEFAULT_MAP_ID;
}

export function getActiveMapDefinition(search = window.location.search): MapDefinition {
  return MAP_REGISTRY[getRequestedMapId(search)];
}

export function getDefaultWeatherForMap(mapId: string): WeatherType {
  return MAP_REGISTRY[mapId]?.defaultWeather ?? WeatherType.Clear;
}
